import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import type { QueryClient } from "@tanstack/react-query";
import { z } from "zod";
import {
  SourceEntitySchema,
  GroupEntitySchema,
  CategoryEntitySchema,
  ExpenseTypeEntitySchema,
  PayeeEntitySchema,
  InvoiceEntitySchema,
  InvoiceDetectorsDtoSchema,
} from "./types";
import type {
  SourceEntity,
  GroupEntity,
  CategoryEntity,
  EntityRegistry,
  NewSourceEntity,
  NewGroupEntity,
  NewCategoryEntity,
  ExpenseTypeEntity,
  NewExpenseTypeEntity,
  UpdateSourceEntity,
  UpdateGroupEntity,
  UpdateCategoryEntity,
  UpdateExpenseTypeEntity,
  PayeeEntity,
  NewPayeeEntity,
  UpdatePayeeEntity,
  InvoiceEntity,
  NewInvoiceEntity,
  UpdateInvoiceEntity,
  InvoiceDetectorsDto,
} from "./types";
import { API_BASE, handleResponse, parseResponse } from "./common";
import { fetchWithInterceptor } from "./client";

type RegistryKey = Exclude<keyof EntityRegistry, "invoices">;

const seedEntityCaches = <K extends RegistryKey>(
  queryClient: QueryClient,
  listKey: string,
  registryKey: K,
  created: EntityRegistry[K][string],
) => {
  queryClient.setQueryData<EntityRegistry[K][string][]>([listKey], (old) =>
    old ? [...old, created] : [created],
  );
  // Every cached variant (`["registry", { includeHidden }]`): a bare
  // `setQueryData(["registry"])` matches none of them exactly, so the new
  // entity only arrived with the refetch. It is never hidden, so it belongs in
  // both.
  queryClient.setQueriesData<EntityRegistry>(
    { queryKey: ["registry"] },
    (old) =>
      old
        ? {
            ...old,
            [registryKey]: { ...old[registryKey], [created.name]: created },
          }
        : old,
  );
  queryClient.invalidateQueries({ queryKey: [listKey] });
  queryClient.invalidateQueries({ queryKey: ["registry"] });
};

// After an entity edit, refresh both the per-type list and the shared
// ["registry"] cache. The registry backs RegistryContext, which resolves
// entity ids to names in transaction/invoice detail views — invalidating only
// the list would leave those views showing the pre-edit name until reload.
const invalidateEntityCaches = (queryClient: QueryClient, listKey: string) => {
  queryClient.invalidateQueries({ queryKey: [listKey] });
  queryClient.invalidateQueries({ queryKey: ["registry"] });
};

// Sources
export const useSources = () => {
  return useQuery<SourceEntity[]>({
    queryKey: ["sources"],
    queryFn: async () => {
      const response = await fetchWithInterceptor(`${API_BASE}/lists/sources`);
      await handleResponse(response);
      return parseResponse(
        z.array(SourceEntitySchema),
        await response.json(),
        "GET /lists/sources",
      );
    },
  });
};

export const useAddSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (source: NewSourceEntity): Promise<SourceEntity> => {
      const response = await fetchWithInterceptor(`${API_BASE}/lists/sources`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(source),
      });
      await handleResponse(response);
      return parseResponse(
        SourceEntitySchema,
        await response.json(),
        "POST /lists/sources",
      );
    },
    onSuccess: (created) =>
      seedEntityCaches(queryClient, "sources", "sources", created),
  });
};

export const useUpdateSource = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (source: UpdateSourceEntity) => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/lists/sources/${source.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(source),
        },
      );
      await handleResponse(response);
    },
    onSuccess: () => invalidateEntityCaches(queryClient, "sources"),
  });
};

// Groups
export const useGroups = () => {
  return useQuery<GroupEntity[]>({
    queryKey: ["groups"],
    queryFn: async () => {
      const response = await fetchWithInterceptor(`${API_BASE}/lists/groups`);
      await handleResponse(response);
      return parseResponse(
        z.array(GroupEntitySchema),
        await response.json(),
        "GET /lists/groups",
      );
    },
  });
};

export const useAddGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (group: NewGroupEntity): Promise<GroupEntity> => {
      const response = await fetchWithInterceptor(`${API_BASE}/lists/groups`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(group),
      });
      await handleResponse(response);
      return parseResponse(
        GroupEntitySchema,
        await response.json(),
        "POST /lists/groups",
      );
    },
    onSuccess: (created) =>
      seedEntityCaches(queryClient, "groups", "groups", created),
  });
};

export const useUpdateGroup = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (group: UpdateGroupEntity) => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/lists/groups/${group.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(group),
        },
      );
      await handleResponse(response);
    },
    onSuccess: () => invalidateEntityCaches(queryClient, "groups"),
  });
};

// Categories
export const useCategories = () => {
  return useQuery<CategoryEntity[]>({
    queryKey: ["categories"],
    queryFn: async () => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/lists/categories`,
      );
      await handleResponse(response);
      return parseResponse(
        z.array(CategoryEntitySchema),
        await response.json(),
        "GET /lists/categories",
      );
    },
  });
};

export const useAddCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      category: NewCategoryEntity,
    ): Promise<CategoryEntity> => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/lists/categories`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(category),
        },
      );
      await handleResponse(response);
      return parseResponse(
        CategoryEntitySchema,
        await response.json(),
        "POST /lists/categories",
      );
    },
    onSuccess: (created) =>
      seedEntityCaches(queryClient, "categories", "categories", created),
  });
};

// Expense Types
export const useExpenseTypes = () => {
  return useQuery<ExpenseTypeEntity[]>({
    queryKey: ["expense-types"],
    queryFn: async () => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/lists/expense-types`,
      );
      await handleResponse(response);
      return parseResponse(
        z.array(ExpenseTypeEntitySchema),
        await response.json(),
        "GET /lists/expense-types",
      );
    },
  });
};

export const useAddExpenseType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (
      expenseType: NewExpenseTypeEntity,
    ): Promise<ExpenseTypeEntity> => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/lists/expense-types`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(expenseType),
        },
      );
      await handleResponse(response);
      return parseResponse(
        ExpenseTypeEntitySchema,
        await response.json(),
        "POST /lists/expense-types",
      );
    },
    onSuccess: (created) =>
      seedEntityCaches(queryClient, "expense-types", "expenseTypes", created),
  });
};

export const useUpdateExpenseType = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (expenseType: UpdateExpenseTypeEntity) => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/lists/expense-types/${expenseType.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(expenseType),
        },
      );
      await handleResponse(response);
    },
    onSuccess: () => invalidateEntityCaches(queryClient, "expense-types"),
  });
};

export const useUpdateCategory = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (category: UpdateCategoryEntity) => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/lists/categories/${category.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(category),
        },
      );
      await handleResponse(response);
    },
    onSuccess: () => invalidateEntityCaches(queryClient, "categories"),
  });
};

// Payees — the vendors an invoice can come from. A label registry like groups
// and categories; the user's own accounts stay in sources.
export const usePayees = () => {
  return useQuery<PayeeEntity[]>({
    queryKey: ["payees"],
    queryFn: async () => {
      const response = await fetchWithInterceptor(`${API_BASE}/lists/payees`);
      await handleResponse(response);
      return parseResponse(
        z.array(PayeeEntitySchema),
        await response.json(),
        "GET /lists/payees",
      );
    },
  });
};

export const useAddPayee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payee: NewPayeeEntity): Promise<PayeeEntity> => {
      const response = await fetchWithInterceptor(`${API_BASE}/lists/payees`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payee),
      });
      await handleResponse(response);
      return parseResponse(
        PayeeEntitySchema,
        await response.json(),
        "POST /lists/payees",
      );
    },
    onSuccess: (created) =>
      seedEntityCaches(queryClient, "payees", "payees", created),
  });
};

export const useUpdatePayee = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payee: UpdatePayeeEntity) => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/lists/payees/${payee.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payee),
        },
      );
      await handleResponse(response);
    },
    onSuccess: () => invalidateEntityCaches(queryClient, "payees"),
  });
};

// Invoices
export const useInvoices = () => {
  return useQuery<InvoiceEntity[]>({
    queryKey: ["invoices"],
    queryFn: async () => {
      const response = await fetchWithInterceptor(`${API_BASE}/lists/invoices`);
      await handleResponse(response);
      return parseResponse(
        z.array(InvoiceEntitySchema),
        await response.json(),
        "GET /lists/invoices",
      );
    },
  });
};

// Invoice PDF auto-fill config: the detectors that auto-fill, plus the layouts
// we recognise but can't (scrambled-font PDFs). Static config served by the
// backend; the browser does the matching. Rarely changes, so cache it
// aggressively.
export const useInvoiceDetectors = () => {
  return useQuery<InvoiceDetectorsDto>({
    queryKey: ["invoice-detectors"],
    staleTime: Infinity,
    queryFn: async () => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/invoice-detectors`,
      );
      await handleResponse(response);
      return parseResponse(
        InvoiceDetectorsDtoSchema,
        await response.json(),
        "GET /invoice-detectors",
      );
    },
  });
};

export type AddInvoiceVariables = {
  invoice: NewInvoiceEntity;
  /**
   * Create even though the vendor's `duplicateKey` rule matched an existing
   * invoice (the server answered `invoiceDuplicate`). Sent as a query flag, as
   * for imports, so it never lands in the stored entity.
   */
  force?: boolean;
};

export const useAddInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      invoice,
      force = false,
    }: AddInvoiceVariables): Promise<InvoiceEntity> => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/lists/invoices${force ? "?force=true" : ""}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(invoice),
        },
      );
      await handleResponse(response);
      return parseResponse(
        InvoiceEntitySchema,
        await response.json(),
        "POST /lists/invoices",
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["attachments"] });
      queryClient.invalidateQueries({ queryKey: ["registry"] });
    },
  });
};

export const useUpdateInvoice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (invoice: UpdateInvoiceEntity) => {
      const response = await fetchWithInterceptor(
        `${API_BASE}/lists/invoices/${invoice.id}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(invoice),
        },
      );
      await handleResponse(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["invoices"] });
      queryClient.invalidateQueries({ queryKey: ["attachments"] });
      queryClient.invalidateQueries({ queryKey: ["registry"] });
    },
  });
};
